export const AI_PROPOSAL_MANAGER_SOLUTION_KEY = 'ai-proposal-manager';
export const AI_PROPOSAL_MANAGER_SOLUTION_NAME = 'AI Proposal Manager';
export const AI_PROPOSAL_MANAGER_CLASSIFICATION_LABEL = 'Specialised';
export const AI_PROPOSAL_MANAGER_SENDER_NAME = AI_PROPOSAL_MANAGER_SOLUTION_NAME;
export const AI_PROPOSAL_MANAGER_SENDER_EMAIL = 'ai-proposal-manager@totalaccess.co.za';
export const AI_PROPOSAL_MANAGER_MENTION_ALIASES = ['@aiproposal', '@aiproposals', '@proposalai'];
export const AI_PROPOSAL_MANAGER_COMMAND_ALIASES = ['/proposal', '/aiproposal'];
export const AI_PROPOSAL_MANAGER_THREAD_HINT = 'No @aiproposal needed — every message here is treated as an AI Proposal request.';

export type AiProposalManagerStartingStage = 'draft' | 'review';
export type AiProposalManagerSourceType = 'site_based' | 'standalone';
export type AiProposalManagerDeliveryChannel = 'email' | 'sms' | 'whatsapp' | 'share_link';
export type AiProposalManagerConversionTarget = 'quote' | 'invoice';
export type AiProposalManagerReferenceDocumentSetting = 'uploads' | 'ai_parsing' | 'include_in_exports';

export interface AiProposalManagerClientSettings {
  startingStage: AiProposalManagerStartingStage;
  defaultValidityDays: number;
  defaultTaxRate: number;
  sourceTypes: {
    siteBased: boolean;
    standalone: boolean;
  };
  deliveryChannels: {
    email: boolean;
    sms: boolean;
    whatsapp: boolean;
    shareLink: boolean;
  };
  conversionTargets: {
    quote: boolean;
    invoice: boolean;
  };
  referenceDocuments: {
    uploads: boolean;
    aiParsing: boolean;
    includeInExports: boolean;
  };
}

export const AI_PROPOSAL_MANAGER_STARTING_STAGE_OPTIONS: Array<{
  value: AiProposalManagerStartingStage;
  label: string;
  description: string;
}> = [
    {
      value: 'draft',
      label: 'Start in draft',
      description: 'New proposals open as editable drafts so teams can refine the scope before internal review.',
    },
    {
      value: 'review',
      label: 'Start in review',
      description: 'New proposals enter internal review immediately, ideal when proposals should be checked before further edits or client delivery.',
    },
  ];

export const AI_PROPOSAL_MANAGER_SOURCE_TYPE_LABELS: Record<AiProposalManagerSourceType, { label: string; description: string }> = {
  site_based: {
    label: 'Site-based proposals',
    description: 'Allow proposals generated from work items, inspections, appointments, and linked site evidence.',
  },
  standalone: {
    label: 'Standalone proposals',
    description: 'Allow greenfield proposals that are created without a linked field-service work item.',
  },
};

export const AI_PROPOSAL_MANAGER_DELIVERY_CHANNEL_LABELS: Record<AiProposalManagerDeliveryChannel, { label: string; description: string }> = {
  email: {
    label: 'Email delivery',
    description: 'Allow proposals to be sent directly to clients by email.',
  },
  sms: {
    label: 'SMS delivery',
    description: 'Allow compact proposal notifications to be sent by SMS.',
  },
  whatsapp: {
    label: 'WhatsApp delivery',
    description: 'Allow proposals to be shared through WhatsApp messaging.',
  },
  share_link: {
    label: 'Share-link delivery',
    description: 'Allow proposals to be shared as a secure review link without immediate messaging.',
  },
};

export const AI_PROPOSAL_MANAGER_CONVERSION_TARGET_LABELS: Record<AiProposalManagerConversionTarget, { label: string; description: string }> = {
  quote: {
    label: 'Convert to quote',
    description: 'Allow approved proposals to generate finance quotes.',
  },
  invoice: {
    label: 'Convert to invoice',
    description: 'Allow approved proposals to convert straight into invoices when that matches the client workflow.',
  },
};

export const AI_PROPOSAL_MANAGER_REFERENCE_DOCUMENT_LABELS: Record<AiProposalManagerReferenceDocumentSetting, { label: string; description: string }> = {
  uploads: {
    label: 'Allow reference document uploads',
    description: 'Show the attachment control in AI Proposal Assistant so staff can upload PDFs, DOCX files, images, and text documents.',
  },
  ai_parsing: {
    label: 'Parse uploaded references into AI context',
    description: 'Extract text from supported uploads and feed that material into the proposal AI prompt to ground scope, pricing, and assumptions.',
  },
  include_in_exports: {
    label: 'Include references in proposal exports',
    description: 'Add uploaded reference document summaries to proposal reports and exported PDFs so reviewers can see what informed the draft.',
  },
};

export const DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS: AiProposalManagerClientSettings = {
  startingStage: 'draft',
  defaultValidityDays: 30,
  defaultTaxRate: 15,
  sourceTypes: {
    siteBased: true,
    standalone: true,
  },
  deliveryChannels: {
    email: true,
    sms: false,
    whatsapp: true,
    shareLink: true,
  },
  conversionTargets: {
    quote: true,
    invoice: true,
  },
  referenceDocuments: {
    uploads: true,
    aiParsing: true,
    includeInExports: true,
  },
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function asBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function clampNumber(value: unknown, fallback: number, minimum: number, maximum: number): number {
  const numericValue = typeof value === 'number'
    ? value
    : typeof value === 'string'
      ? Number.parseFloat(value)
      : Number.NaN;

  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  return Math.min(maximum, Math.max(minimum, Number(numericValue.toFixed(2))));
}

export function getAiProposalManagerClientSettings(
  value?: unknown,
): AiProposalManagerClientSettings {
  const record = asRecord(value);
  const startingStage = record?.startingStage === 'review' ? 'review' : 'draft';
  const rawSourceTypes = asRecord(record?.sourceTypes);
  const rawDeliveryChannels = asRecord(record?.deliveryChannels);
  const rawConversionTargets = asRecord(record?.conversionTargets);
  const rawReferenceDocuments = asRecord(record?.referenceDocuments);

  return {
    startingStage,
    defaultValidityDays: clampNumber(
      record?.defaultValidityDays,
      DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.defaultValidityDays,
      1,
      365,
    ),
    defaultTaxRate: clampNumber(
      record?.defaultTaxRate,
      DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.defaultTaxRate,
      0,
      100,
    ),
    sourceTypes: {
      siteBased: asBoolean(rawSourceTypes?.siteBased, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.sourceTypes.siteBased),
      standalone: asBoolean(rawSourceTypes?.standalone, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.sourceTypes.standalone),
    },
    deliveryChannels: {
      email: asBoolean(rawDeliveryChannels?.email, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.deliveryChannels.email),
      sms: asBoolean(rawDeliveryChannels?.sms, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.deliveryChannels.sms),
      whatsapp: asBoolean(rawDeliveryChannels?.whatsapp, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.deliveryChannels.whatsapp),
      shareLink: asBoolean(rawDeliveryChannels?.shareLink, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.deliveryChannels.shareLink),
    },
    conversionTargets: {
      quote: asBoolean(rawConversionTargets?.quote, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.conversionTargets.quote),
      invoice: asBoolean(rawConversionTargets?.invoice, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.conversionTargets.invoice),
    },
    referenceDocuments: {
      uploads: asBoolean(rawReferenceDocuments?.uploads, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.referenceDocuments.uploads),
      aiParsing: asBoolean(rawReferenceDocuments?.aiParsing, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.referenceDocuments.aiParsing),
      includeInExports: asBoolean(rawReferenceDocuments?.includeInExports, DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS.referenceDocuments.includeInExports),
    },
  };
}

export function isAiProposalManagerSourceTypeEnabled(
  sourceType: AiProposalManagerSourceType,
  settings?: AiProposalManagerClientSettings | null,
): boolean {
  const resolvedSettings = settings || DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS;
  return sourceType === 'site_based'
    ? resolvedSettings.sourceTypes.siteBased
    : resolvedSettings.sourceTypes.standalone;
}

export function isAiProposalManagerDeliveryChannelEnabled(
  channel: AiProposalManagerDeliveryChannel,
  settings?: AiProposalManagerClientSettings | null,
): boolean {
  const resolvedSettings = settings || DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS;

  switch (channel) {
    case 'email':
      return resolvedSettings.deliveryChannels.email;
    case 'sms':
      return resolvedSettings.deliveryChannels.sms;
    case 'whatsapp':
      return resolvedSettings.deliveryChannels.whatsapp;
    case 'share_link':
      return resolvedSettings.deliveryChannels.shareLink;
    default:
      return true;
  }
}

export function isAiProposalManagerConversionTargetEnabled(
  target: AiProposalManagerConversionTarget,
  settings?: AiProposalManagerClientSettings | null,
): boolean {
  const resolvedSettings = settings || DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS;
  return target === 'quote'
    ? resolvedSettings.conversionTargets.quote
    : resolvedSettings.conversionTargets.invoice;
}

export function isAiProposalManagerReferenceUploadsEnabled(
  settings?: AiProposalManagerClientSettings | null,
): boolean {
  const resolvedSettings = settings || DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS;
  return resolvedSettings.referenceDocuments.uploads;
}

export function isAiProposalManagerReferenceParsingEnabled(
  settings?: AiProposalManagerClientSettings | null,
): boolean {
  const resolvedSettings = settings || DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS;
  return resolvedSettings.referenceDocuments.aiParsing;
}

export function isAiProposalManagerReferenceExportEnabled(
  settings?: AiProposalManagerClientSettings | null,
): boolean {
  const resolvedSettings = settings || DEFAULT_AI_PROPOSAL_MANAGER_CLIENT_SETTINGS;
  return resolvedSettings.referenceDocuments.includeInExports;
}

export type AiProposalManagerPromptTrigger = 'mention' | 'command' | 'none';

interface AiProposalManagerThreadLike {
  solution_key?: string | null;
  thread_type?: string | null;
  thread_name?: string | null;
  title?: string | null;
}

export function isAiProposalManagerSolutionKey(solutionKey?: string | null): boolean {
  return typeof solutionKey === 'string'
    && solutionKey.trim().toLowerCase() === AI_PROPOSAL_MANAGER_SOLUTION_KEY;
}

function escapeRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function buildAliasPattern(aliases: string[]): string {
  return aliases.map((alias) => escapeRegex(alias)).join('|');
}

function normalizePromptText(value: string): string {
  return value
    .replace(/\s+/g, ' ')
    .trim();
}

function stripCommandInvocation(message: string): string | null {
  const commandPattern = new RegExp(`^(?:${buildAliasPattern(AI_PROPOSAL_MANAGER_COMMAND_ALIASES)})(?=$|[\\s,:;.!?\\-])`, 'i');
  const match = commandPattern.exec(message);
  if (!match) return null;

  return normalizePromptText(message.slice(match[0].length).replace(/^[\s,:;.!?\-]+/, ''));
}

function stripMentionInvocation(message: string): string | null {
  const mentionPattern = new RegExp(`(^|\\s)(?:${buildAliasPattern(AI_PROPOSAL_MANAGER_MENTION_ALIASES)})(?=$|[\\s,:;.!?\\-])`, 'i');
  const match = mentionPattern.exec(message);
  if (!match) return null;

  const before = message.slice(0, match.index).trim();
  const after = message
    .slice(match.index + match[0].length)
    .replace(/^[\s,:;.!?\-]+/, '')
    .trim();

  return normalizePromptText([before, after].filter(Boolean).join(' '));
}

export function getAiProposalManagerPromptFromMessage(message?: string | null): {
  isInvocation: boolean;
  shouldRespond: boolean;
  prompt: string | null;
  trigger: AiProposalManagerPromptTrigger;
} {
  const trimmedMessage = typeof message === 'string' ? message.trim() : '';
  if (!trimmedMessage) {
    return {
      isInvocation: false,
      shouldRespond: false,
      prompt: null,
      trigger: 'none',
    };
  }

  const lowercaseMessage = trimmedMessage.toLowerCase();

  for (const alias of AI_PROPOSAL_MANAGER_COMMAND_ALIASES) {
    if (!lowercaseMessage.startsWith(alias)) continue;

    const prompt = stripCommandInvocation(trimmedMessage);
    if (prompt === null) continue;

    return {
      isInvocation: true,
      shouldRespond: prompt.length > 0,
      prompt: prompt || null,
      trigger: 'command',
    };
  }

  for (const alias of AI_PROPOSAL_MANAGER_MENTION_ALIASES) {
    if (!lowercaseMessage.includes(alias)) continue;

    const prompt = stripMentionInvocation(trimmedMessage);
    if (prompt === null) continue;

    return {
      isInvocation: true,
      shouldRespond: prompt.length > 0,
      prompt: prompt || null,
      trigger: 'mention',
    };
  }

  return {
    isInvocation: false,
    shouldRespond: false,
    prompt: null,
    trigger: 'none',
  };
}

export function isAiProposalManagerThread(thread?: AiProposalManagerThreadLike | null): boolean {
  if (!thread) return false;

  const solutionKey = typeof thread.solution_key === 'string'
    ? thread.solution_key.trim().toLowerCase()
    : '';

  if (isAiProposalManagerSolutionKey(solutionKey)) {
    return true;
  }

  const threadName = [thread.thread_name, thread.title]
    .find((value): value is string => typeof value === 'string' && value.trim().length > 0)
    ?.trim()
    .toLowerCase();

  return thread.thread_type === 'team' && threadName === AI_PROPOSAL_MANAGER_SOLUTION_NAME.toLowerCase();
}

export function getAiProposalManagerDefaultFeaturePolicies(solutionKey?: string | null) {
  if (isAiProposalManagerSolutionKey(solutionKey)) {
    return {
      message_centre: true,
      job_threads: false,
      video_calls: false,
      voice_calls: false,
      my_tasks: true,
      total_assistant: true,
      show_when_online: true,
      ai_proposal_view_tab: true,
      ai_proposal_generate: true,
      ai_proposal_approve: false,
    };
  }

  return {
    message_centre: true,
    job_threads: true,
    video_calls: true,
    voice_calls: true,
    my_tasks: true,
    total_assistant: true,
    show_when_online: true,
  };
}

const AI_PROPOSAL_MANAGER_SENDER_AVATAR_SVG = encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="96" height="96" viewBox="0 0 96 96" fill="none">
    <defs>
      <linearGradient id="aiProposalGradient" x1="10" y1="10" x2="86" y2="86" gradientUnits="userSpaceOnUse">
        <stop stop-color="#7c3aed"/>
        <stop offset="1" stop-color="#ec4899"/>
      </linearGradient>
    </defs>
    <rect width="96" height="96" rx="48" fill="url(#aiProposalGradient)"/>
    <path d="M26 30C26 25.582 29.582 22 34 22H62C66.418 22 70 25.582 70 30V60C70 64.418 66.418 68 62 68H42L30 78V68H34C29.582 68 26 64.418 26 60V30Z" fill="white" fill-opacity="0.16"/>
    <text x="48" y="57" text-anchor="middle" font-size="24" font-family="Inter, Arial, sans-serif" font-weight="700" fill="white">AI</text>
  </svg>`.trim(),
);
export const AI_PROPOSAL_MANAGER_SENDER_AVATAR = `data:image/svg+xml;charset=UTF-8,${AI_PROPOSAL_MANAGER_SENDER_AVATAR_SVG}`;
